import { Radio } from 'antd'
import { useMemo, useState, type FC } from 'react'
import { FaCalendarAlt, FaLayerGroup } from 'react-icons/fa'

import {
  Alert,
  Button,
  DateFormatter,
  Modal,
  ModalBody,
  ModalFooter,
  NormalizedDatePicker
} from '@/components/elements'
import {
  ACTIONS,
  DESCRIPTIONS,
  LABELS,
  MENU
} from '@/constants/common/message.constants'
import { type ShipmentOrdersListExEntity } from '@/libs/api'
import { safeParseISO } from '@/libs/utils'

import {
  collectBundleDateOptions,
  KEEP_BUNDLE_DATE,
  resolveBundleDateValue,
  toIncomeRecordingDateHandling,
  validateBundleDateSelection,
  type BundleDateAlignMode,
  type BundleDateOption,
  type IncomeRecordingDateMode
} from '../../models/bundle-date-alignment'

const DATE_FORMAT = 'yyyy/MM/dd'

/**
 * 1日付項目分の選択状態。
 * mode の 'follow' は請求計上日の行だけが使う（発日・着日の行は align / keep の2択）
 */
type DateRowSelection = {
  mode: IncomeRecordingDateMode
  /** 揃える日付。カレンダー選択も手入力も同じここに入る */
  date: Date | null
  /** 手入力が日付として解釈できなかったときのメッセージ */
  inputError: string | null
}

/**
 * 揃えるのが既定。揃える先は選択中でいちばん若い日付を初期値に置く
 * （collectBundleDateOptions は日付の昇順で返す）。
 */
const createInitialSelection = (
  options: BundleDateOption[]
): DateRowSelection => ({
  mode: 'align',
  date: safeParseISO(options[0]?.value),
  inputError: null
})

/**
 * 請求計上日の初期値は「発日・着日に追従」。
 * 揃える日付には切り替えたときのためにいちばん若い現在値を置いておく。
 */
const createInitialIncomeSelection = (
  options: BundleDateOption[]
): DateRowSelection => ({
  mode: 'follow',
  date: safeParseISO(options[0]?.value),
  inputError: null
})

/** 「日付を揃える」のまま揃える先を空にした状態 */
const lacksAlignDate = (selection: DateRowSelection): boolean =>
  selection.mode === 'align' && selection.date === null

type DateSelectionRowProps = {
  label: string
  options: BundleDateOption[]
  selection: DateRowSelection
  onChange: (selection: DateRowSelection) => void
  testId: string
  /**
   * 指定すると「発日・着日に追従」の選択肢を先頭に出す（請求計上日の行で使う）。
   * ラベル文字列がそのままラジオの表示になる
   */
  followLabel?: string
}

/**
 * 1日付項目分の選択行。
 * 上段に選択中の依頼が持つ日付（ユニーク値）を並べ、下段で「日付を揃える／そのまま」
 * （請求計上日の行はさらに「発日・着日に追従」）を選ぶ。
 * 揃える日付は候補に限らずカレンダーから自由に指定できる。
 */
const DateSelectionRow: FC<DateSelectionRowProps> = ({
  label,
  options,
  selection,
  onChange,
  testId,
  followLabel
}) => {
  // 揃える先が空なだけの状態はボタンの活性で示し、メッセージは出さない
  const error = selection.inputError

  return (
    <div className="grid grid-cols-[4rem_1fr] items-start gap-2">
      <div className="pt-1 font-bold">{label}</div>
      <div className="grid gap-1">
        <div data-testid={`${testId}-current`}>
          {options.map((option, index) => (
            <span key={option.key}>
              {index > 0 && ', '}
              <DateFormatter dateFormat={DATE_FORMAT} value={option.value} />
            </span>
          ))}
        </div>
        <Radio.Group
          // 選択肢は縦に積む（横並びだとラベルが折り返して読み取りづらい）
          className="flex flex-col items-start gap-1 [&_label]:mr-0"
          value={selection.mode}
          onChange={(event) =>
            onChange({ ...selection, mode: event.target.value })
          }
          data-testid={testId}
        >
          {followLabel && (
            <Radio
              className="whitespace-nowrap"
              value={'follow' satisfies IncomeRecordingDateMode}
            >
              {followLabel}
            </Radio>
          )}
          <div className="flex items-center gap-2">
            <Radio
              className="whitespace-nowrap"
              value={'align' satisfies BundleDateAlignMode}
            >
              {ACTIONS.ALIGN_DATE}
            </Radio>
            <NormalizedDatePicker
              className="w-36 shrink-0"
              // 「日付を揃える」以外を選んでいる間は揃える日付を持たないのでカレンダーは触らせない
              disabled={selection.mode !== 'align'}
              value={selection.date}
              onChange={(date) =>
                onChange({ ...selection, date, inputError: null })
              }
              onInvalidText={(message) =>
                onChange({ ...selection, date: null, inputError: message })
              }
              onValidText={() => onChange({ ...selection, inputError: null })}
              data-testid={`${testId}-picker`}
            />
          </div>
          <Radio
            className="whitespace-nowrap"
            value={'keep' satisfies BundleDateAlignMode}
          >
            {ACTIONS.KEEP_DATE_AS_IS}
          </Radio>
        </Radio.Group>
        {error && (
          <div className="text-red-600" data-testid={`${testId}-error`}>
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

type Props = {
  /** 日付変更（または束ね）の対象に選択されている配送依頼 */
  orders: ShipmentOrdersListExEntity[]
  bundling: boolean
  onCancel: () => void
  onConfirm: (dates: {
    shippingDate?: string
    deliveryDate?: string
    incomeRecordingDateHandling: 'FOLLOW' | 'ALIGN' | 'KEEP'
    incomeRecordingDate?: string
  }) => void | Promise<void>
  /**
   * 表示と確定条件の切り替え。
   * - 'bundle'（既定）: 束ね作成時の日付統一。両方「そのまま」でも束ねは実行できる
   * - 'update': 選択した依頼の日付一括変更。何も変わらない指定では確定できない
   */
  variant?: 'bundle' | 'update'
  /**
   * Drawer 配下で開くときの重なり順の固定用（配車板の配送依頼ドロワーが
   * VEHICLE_DISPATCH_BOARDS_Z_INDEX.MODAL_IN_DRAWER を渡す）。未指定なら antd に委任
   */
  zIndex?: number
}

/**
 * 発日・着日・請求計上日をまとめて指定するモーダル。
 * 束ね作成時（variant: 'bundle'）は発日・着日のいずれかが複数日付になる選択で
 * まとめようとしたときに開き、「そのまま」か揃える日付かを項目ごとに選ばせてから
 * 束ねを実行する。選択した依頼の日付一括変更（variant: 'update'）でも同じ選択 UI を使う。
 */
export const ShipmentOrderBundleDateAlignModal: FC<Props> = ({
  orders,
  bundling,
  onCancel,
  onConfirm,
  variant = 'bundle',
  zIndex
}) => {
  const {
    shippingDateOptions,
    deliveryDateOptions,
    incomeRecordingDateOptions
  } = useMemo(() => collectBundleDateOptions(orders), [orders])
  const [shippingSelection, setShippingSelection] = useState<DateRowSelection>(
    () => createInitialSelection(shippingDateOptions)
  )
  const [deliverySelection, setDeliverySelection] = useState<DateRowSelection>(
    () => createInitialSelection(deliveryDateOptions)
  )
  const [incomeSelection, setIncomeSelection] = useState<DateRowSelection>(() =>
    createInitialIncomeSelection(incomeRecordingDateOptions)
  )

  const shippingDate =
    shippingSelection.mode === 'align'
      ? resolveBundleDateValue(shippingDateOptions, shippingSelection.date)
      : KEEP_BUNDLE_DATE
  const deliveryDate =
    deliverySelection.mode === 'align'
      ? resolveBundleDateValue(deliveryDateOptions, deliverySelection.date)
      : KEEP_BUNDLE_DATE
  const incomeRecordingDate =
    incomeSelection.mode === 'align'
      ? resolveBundleDateValue(incomeRecordingDateOptions, incomeSelection.date)
      : KEEP_BUNDLE_DATE

  const validationError = useMemo(
    () => validateBundleDateSelection({ orders, shippingDate, deliveryDate }),
    [orders, shippingDate, deliveryDate]
  )

  // isManual = false はインポート由来（外部キー付き）の依頼。編集にリンク操作が要る
  // 依頼が混ざっていることを、束ねる前に気付けるようにする
  const hasImportedOrder = orders.some((order) => !order.isManual)

  // 日付の入力自体が済んでいない間は束ねを実行させない
  const hasIncompleteInput =
    lacksAlignDate(shippingSelection) ||
    lacksAlignDate(deliverySelection) ||
    lacksAlignDate(incomeSelection) ||
    shippingSelection.inputError !== null ||
    deliverySelection.inputError !== null ||
    incomeSelection.inputError !== null

  // 日付一括変更で発日・着日とも「そのまま」の場合、計上日の追従・据え置きは
  // どちらも何も変えないため、計上日を揃えるとき以外は確定させない
  const isNoopUpdate =
    variant === 'update' &&
    shippingSelection.mode === 'keep' &&
    deliverySelection.mode === 'keep' &&
    incomeSelection.mode !== 'align'

  return (
    <Modal
      title={
        variant === 'update'
          ? MENU.CHANGING_DATES_IN_BULK
          : MENU.ALIGNING_BUNDLE_DATES
      }
      // 注意書きを 1 行に収めたいので選択行に必要な幅より少し広くとる
      width={560}
      centered
      open
      onCancel={onCancel}
      zIndex={zIndex}
    >
      <ModalBody>
        <div className="grid gap-3">
          {hasImportedOrder && (
            // 配送依頼の編集モーダルの注意書きと同じ見た目（赤枠・黒字）に揃える
            <Alert color="red" data-testid="bundle-date-align-imported-notice">
              {DESCRIPTIONS.INCLUDES_IMPORTED_SHIPMENT_ORDERS}
            </Alert>
          )}
          <div>
            {variant === 'update'
              ? DESCRIPTIONS.BULK_DATES_CHANGE
              : DESCRIPTIONS.BUNDLE_DATES_MIXED}
          </div>
          <DateSelectionRow
            label={LABELS.SHIPPING_DATE_SHORT}
            options={shippingDateOptions}
            selection={shippingSelection}
            onChange={setShippingSelection}
            testId={`bundle-date-align-${LABELS.SHIPPING_DATE_SHORT}`}
          />
          <DateSelectionRow
            label={LABELS.DELIVERY_DATE}
            options={deliveryDateOptions}
            selection={deliverySelection}
            onChange={setDeliverySelection}
            testId={`bundle-date-align-${LABELS.DELIVERY_DATE}`}
          />
          <DateSelectionRow
            label={LABELS.INCOME_RECORDING_DATE_SHORT}
            options={incomeRecordingDateOptions}
            selection={incomeSelection}
            onChange={setIncomeSelection}
            testId={`bundle-date-align-${LABELS.INCOME_RECORDING_DATE_SHORT}`}
            followLabel={ACTIONS.FOLLOW_CUSTOMER_SETTING}
          />
          {validationError && (
            <div className="text-red-600" data-testid="bundle-date-align-error">
              {validationError}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel}>{ACTIONS.CANCEL}</Button>
        <Button
          type="primary"
          icon={variant === 'update' ? <FaCalendarAlt /> : <FaLayerGroup />}
          loading={bundling}
          disabled={
            validationError !== null || hasIncompleteInput || isNoopUpdate
          }
          onClick={() =>
            onConfirm({
              shippingDate: shippingDate || undefined,
              deliveryDate: deliveryDate || undefined,
              incomeRecordingDateHandling: toIncomeRecordingDateHandling(
                incomeSelection.mode
              ),
              incomeRecordingDate: incomeRecordingDate || undefined
            })
          }
          data-testid="bundle-date-align-confirm"
        >
          {variant === 'update' ? ACTIONS.BULK_UPDATE_DATES : ACTIONS.BUNDLE}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
